/**
 * When may the app call the API, and what does it show while it decides?
 *
 * Under msal this was subtle: `accounts` was seeded synchronously from the
 * session cache while a token was still being fetched, so "an account exists"
 * was not "a credential exists" (see ADR-005 and the retired state machine).
 * With a server-side session there is only one question — does this browser
 * hold a session the server accepts? — and exactly one way to answer it: ask.
 *
 * Two probes settle it, both anonymous:
 *   /api/auth/config-check  which mode the server is in
 *   /api/auth/me            whether this browser is somebody
 *
 * `ready` means both have answered. Showing the sign-in screen before that is
 * what made a signed-in user bounce, so the shell waits instead.
 */
export interface SessionInputs {
  /** /api/auth/config-check has answered (either way). */
  configResolved: boolean;
  /** /api/auth/me has answered (either way). */
  sessionResolved: boolean;
  /** /api/auth/me returned a principal. */
  hasSession: boolean;
}

export interface AuthResolution {
  /** Both probes are in: render the app or the sign-in screen, not a splash. */
  ready: boolean;
  /** A usable credential exists — only now may queries run. */
  signedIn: boolean;
}

export function resolveSessionAuth({
  configResolved,
  sessionResolved,
  hasSession,
}: SessionInputs): AuthResolution {
  if (!configResolved || !sessionResolved) return { ready: false, signedIn: false };
  return { ready: true, signedIn: hasSession };
}
